/**
 * Broker identity verification via Nafath.
 *
 * Flow:
 *  1. Broker enters their 10-digit national ID.
 *  2. POST /api/nafath/initiate → backend returns { transId, random }.
 *  3. We show `random` in big numbers; broker opens Nafath app on phone,
 *     sees three numbers, taps the one matching `random`.
 *  4. We poll POST /api/nafath/status every 3s for up to ~5min.
 *  5. On COMPLETED: backend chains into FAL verification automatically; we
 *     reflect that in the UI and let the broker continue to /properties/new.
 *
 * When NAFATH_APP_ID is unset on the backend, /api/nafath/me returns
 * { available: false } and we render the existing "Coming Soon" card from
 * <NafathVerification />.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import Input from '../../../components/base/Input';
import Button from '../../../components/base/Button';
import NafathVerification, {
  mapApiStatusToNafathStatus,
  type NafathStatus,
} from '../../../components/verification/NafathVerification';
import {
  initiateNafath,
  checkNafathStatus,
  fetchNafathMe,
  type NafathMeResponse,
} from '../../../services/nafathService';
import { ApiError } from '../../../utils/apiClient';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000; // Match backend TTL

export default function VerifyIdentityPage() {
  const { t, i18n } = useTranslation(['verification', 'common']);
  const { session } = useAuth();
  const navigate = useNavigate();
  const isRTL = i18n.dir() === 'rtl';
  const locale: 'ar' | 'en' = isRTL ? 'ar' : 'en';

  const [me, setMe] = useState<NafathMeResponse | null>(null);
  const [meLoading, setMeLoading] = useState(true);
  const [nationalId, setNationalId] = useState('');
  const [status, setStatus] = useState<NafathStatus>('not_started');
  const [verificationCode, setVerificationCode] = useState<string | undefined>();
  const [, setTransId] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStartedAtRef = useRef<number>(0);

  // Load current verification state from backend on mount.
  useEffect(() => {
    if (!session?.token) return;
    let cancelled = false;
    fetchNafathMe(session.token)
      .then((data) => {
        if (cancelled) return;
        setMe(data);
        // If already verified, show the success state directly.
        if (data.nafathVerificationStatus === 'VERIFIED') {
          setStatus('completed');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('Failed to fetch Nafath status', err);
        }
      })
      .finally(() => {
        if (!cancelled) setMeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session?.token]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const poll = useCallback(async (currentTransId: string) => {
    if (!session?.token) return;

    try {
      const result = await checkNafathStatus(currentTransId, session.token);
      const next = mapApiStatusToNafathStatus(result.status);
      setStatus(next);

      if (result.status !== 'WAITING') {
        stopPolling();
        return;
      }

      if (Date.now() - pollStartedAtRef.current >= MAX_POLL_DURATION_MS) {
        setStatus('expired');
        stopPolling();
        return;
      }

      pollTimerRef.current = setTimeout(() => poll(currentTransId), POLL_INTERVAL_MS);
    } catch (err) {
      console.warn('Nafath status poll failed', err);
      // Don't abort the poll loop on a single transient error — keep trying
      // until we hit the local timeout.
      if (Date.now() - pollStartedAtRef.current >= MAX_POLL_DURATION_MS) {
        setStatus('expired');
        stopPolling();
        return;
      }
      pollTimerRef.current = setTimeout(() => poll(currentTransId), POLL_INTERVAL_MS);
    }
  }, [session?.token, stopPolling]);

  useEffect(() => stopPolling, [stopPolling]);

  const handleInitiate = async () => {
    if (!session?.token) return;
    setError(null);

    if (!/^\d{10}$/.test(nationalId)) {
      setError(isRTL
        ? 'يجب أن يتكون رقم الهوية الوطنية من 10 أرقام'
        : 'National ID must be exactly 10 digits');
      return;
    }

    setSubmitting(true);
    try {
      const result = await initiateNafath(nationalId, locale, session.token);
      setTransId(result.transId);
      setVerificationCode(result.random);
      setStatus('waiting');
      pollStartedAtRef.current = Date.now();
      pollTimerRef.current = setTimeout(() => poll(result.transId), POLL_INTERVAL_MS);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setError(isRTL
            ? 'لقد قمت بمحاولات كثيرة. يرجى الانتظار قبل المحاولة مرة أخرى.'
            : 'Too many attempts. Please wait a few minutes and try again.');
        } else if (err.status === 503) {
          setError(isRTL
            ? 'خدمة نفاذ غير متاحة حالياً'
            : 'Nafath service is not yet enabled on this environment.');
        } else {
          setError(err.message);
        }
      } else {
        setError(isRTL ? 'حدث خطأ. حاول مرة أخرى.' : 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    stopPolling();
    setStatus('not_started');
    setVerificationCode(undefined);
    setTransId(undefined);
  };

  const handleRetry = () => {
    setStatus('not_started');
    setVerificationCode(undefined);
    setTransId(undefined);
    setError(null);
  };

  const isAvailable = me?.available ?? false;
  const alreadyVerified = me?.nafathVerificationStatus === 'VERIFIED';

  const heading = useMemo(
    () => (isRTL ? 'التحقق من الهوية' : 'Identity verification'),
    [isRTL],
  );

  return (
    <div className="min-h-screen bg-white" dir={i18n.dir()}>
      <Navigation />
      <main className="max-w-3xl mx-auto px-4 py-12 sm:px-6">
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--pv-color-primary)] mb-2">{heading}</h1>
          <p className="text-gray-600">
            {isRTL
              ? 'قم بالتحقق من هويتك عبر تطبيق نفاذ لتفعيل حساب الوسيط ونشر العقارات.'
              : 'Verify your identity via the Nafath app to activate your broker account and start listing properties.'}
          </p>
        </header>

        {meLoading ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-500">
            {t('common:common.loading', isRTL ? 'جاري التحميل...' : 'Loading...')}
          </div>
        ) : alreadyVerified ? (
          <div className="bg-white rounded-2xl border border-green-200 p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <i className="ri-checkbox-circle-fill text-4xl text-green-500" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-bold text-green-700 mb-2">
              {isRTL ? 'تم التحقق' : 'Identity verified'}
            </h2>
            {me?.nationalIdLastFour && (
              <p className="text-gray-500 mb-2">
                {isRTL ? `الهوية: •••••• ${me.nationalIdLastFour}` : `National ID: •••••• ${me.nationalIdLastFour}`}
              </p>
            )}
            <p className="text-gray-600 mb-6">
              {me?.falVerificationStatus === 'APPROVED'
                ? (isRTL
                    ? 'تم تفعيل ترخيص الفال تلقائياً. يمكنك الآن نشر العقارات.'
                    : 'Your FAL license is approved. You can now list properties.')
                : (isRTL
                    ? 'تم التحقق من هويتك. يخضع ترخيص الفال للمراجعة.'
                    : 'Identity verified. Your FAL license is under review.')}
            </p>
            <Button variant="primary" onClick={() => navigate('/properties/new')}>
              {isRTL ? 'إضافة عقار' : 'Add a property'}
            </Button>
          </div>
        ) : !isAvailable ? (
          // Backend says NAFATH_APP_ID isn't set - show Coming Soon UI.
          <NafathVerification status="not_started" isApiReady={false} />
        ) : status === 'not_started' ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8">
            <p className="text-sm text-gray-700 mb-4">
              {isRTL
                ? 'أدخل رقم هويتك الوطنية أو الإقامة (10 أرقام). سيتم إرسال إشعار إلى تطبيق نفاذ على هاتفك.'
                : 'Enter your 10-digit national ID or Iqama number. A push will be sent to the Nafath app on your phone.'}
            </p>
            <div className="mb-4">
              <label htmlFor="nationalId" className="block text-sm font-medium text-gray-700 mb-1">
                {isRTL ? 'رقم الهوية' : 'National ID'}
              </label>
              <Input
                id="nationalId"
                type="text"
                inputMode="numeric"
                maxLength={10}
                value={nationalId}
                onChange={(e) => setNationalId(e.target.value.replace(/\D/g, ''))}
                placeholder="1xxxxxxxxx"
                disabled={submitting}
              />
            </div>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <Button
              variant="primary"
              onClick={handleInitiate}
              disabled={submitting || nationalId.length !== 10}
              className="w-full sm:w-auto"
            >
              <i className={`ri-fingerprint-line ${isRTL ? 'ml-2' : 'mr-2'}`} aria-hidden="true" />
              {submitting
                ? (isRTL ? 'جاري الإرسال...' : 'Sending...')
                : (isRTL ? 'بدء التحقق عبر نفاذ' : 'Start Nafath verification')}
            </Button>
          </div>
        ) : (
          <NafathVerification
            status={status}
            verificationCode={verificationCode}
            isApiReady
            onCancel={handleCancel}
            onRetry={handleRetry}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}
